/**
 * LD Header Dropdown JavaScript
 * ============================
 * 
 * Handles dropdown menus, mobile menu, and scroll detection
 * 
 * SIMPLIFIED LOGIC:
 * ================
 * 
 * 1. SCROLL DETECTION:
 *    - Adds/removes 'ld-header-03--scrolled' class
 *    - Switches logo (white/black) based on scroll and color mode
 * 
 * 2. DROPDOWN MENUS:
 *    - Opens/closes dropdown overlays
 *    - Adds/removes active class (colors handled by CSS)
 * 
 * 3. MOBILE MENU:
 *    - Burger menu toggle
 *    - Mobile overlay management
 * 
 * 4. CSS HANDLES ALL COLORS:
 *    - White mode: white elements + yellow hover/active
 *    - Black mode: black elements + black hover/active with opacity
 */

class LDHeaderDropdown {
  constructor() {
    this.init();
  }

  init() {
    this.header = document.querySelector('.ld-header-03');
    this.overlay = document.querySelector('.ld-header-03__dropdown-overlay');
    this.toggles = document.querySelectorAll('.ld-header-03__nav-toggle');
    this.submenus = document.querySelectorAll('.ld-header-03__submenu');
    
    // Mobile elements
    this.burger = document.querySelector('.ld-header-03__burger');
    this.mobileOverlay = document.querySelector('.ld-header-03__mobile-overlay');
    this.mobileCart = document.querySelector('.ld-header-03__mobile-cart');
    
    // Early return if required elements don't exist
    if (!this.header) {
      return;
    }

    // Ensure our header has the highest z-index to avoid conflicts
    this.header.style.zIndex = '1000';
    
    this.setupScrollHandler();
    this.setupEventListeners();
    this.setupMobileEventListeners();
    this.initColorStateManagement();
    
    // Initialize header state
    this.initializeHeaderState();
    
    // Initialize default mobile menu states
    this.initializeDefaultMobileMenuStates();
  }

  initializeHeaderState() {
    // Ensure header starts in correct state
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    // Disable transitions initially
    this.header.style.transition = 'none';
    
    // Remove any conflicting classes and add our own
    this.header.classList.remove('ld-header-03--hidden', 'ld-header-03--fixed');
    
    if (scrollTop <= 10) {
      // At top - show header
      this.header.style.transform = 'translateY(0)';
    } else {
      // Scrolled - hide header initially
      this.header.style.transform = 'translateY(calc(-100% - var(--announcement-bar-height, 0px)))';
      this.header.classList.add('ld-header-03--hidden');
    }
    
    // Enable transitions after a short delay to prevent flickering
    setTimeout(() => {
      this.header.style.transition = 'transform 0.3s ease, background-color 0.3s ease';
    }, 100);
  }

  /**
   * Initialize default mobile menu states
   */
  initializeDefaultMobileMenuStates() {
    // Set first mobile nav item as open by default
    const firstMobileNavItem = document.querySelector('.ld-header-03__mobile-nav-item[data-has-submenu="true"]:first-child');
    if (firstMobileNavItem) {
      firstMobileNavItem.classList.add('is-open');
    }

    // Set first submenu toggle as open by default
    const firstSubmenuToggle = document.querySelector('.ld-header-03__mobile-nav-item[data-has-submenu="true"]:first-child .ld-header-03__mobile-submenu-toggle:first-child');
    if (firstSubmenuToggle) {
      firstSubmenuToggle.setAttribute('aria-expanded', 'true');
      const deepSubmenu = firstSubmenuToggle.querySelector('.ld-header-03__mobile-deep-submenu[data-deep-submenu="true"]');
      if (deepSubmenu) {
        deepSubmenu.classList.add('is-open');
      }
    }
  }

  setupScrollHandler() {
    let scrollThreshold = 10; // Start changing header after 10px scroll
    this.logoImage = this.header.querySelector('.ld-header-03__logo-image');
    
    // Variables for smart header behavior
    let lastScrollTop = 0;
    let isHeaderVisible = true;
    let isHeaderFixed = false;
    let scrollDirectionThreshold = 5; // Increased threshold for more stable detection
    let lastScrollTime = Date.now();
    
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const currentTime = Date.now();
      const colorState = getComputedStyle(document.documentElement).getPropertyValue('--ld-header-color-state').trim();
      const isBlackState = colorState === 'black';
      
      // Determine scroll direction with threshold
      const scrollDifference = scrollTop - lastScrollTop;
      const timeDifference = currentTime - lastScrollTime;
      const isScrollingDown = scrollDifference > scrollDirectionThreshold;
      const isScrollingUp = scrollDifference < -scrollDirectionThreshold;
      const isAtTop = scrollTop <= scrollThreshold;
      
      // Smart header behavior - immediate response
      if (isScrollingDown && !isAtTop) {
        // Scrolling down - hide header
        if (isHeaderVisible) {
          this.header.style.transform = 'translateY(calc(-100% - var(--announcement-bar-height, 0px)))';
          this.header.classList.add('ld-header-03--hidden');
          this.header.classList.remove('ld-header-03--fixed');
          isHeaderVisible = false;
          isHeaderFixed = false;
        }
      } else if (isScrollingUp && !isAtTop) {
        // Scrolling up - show and fix header
        if (!isHeaderVisible) {
          this.header.style.transform = 'translateY(0)';
          this.header.classList.remove('ld-header-03--hidden');
          this.header.classList.add('ld-header-03--fixed');
          isHeaderVisible = true;
          isHeaderFixed = true;
        }
      } else if (isAtTop) {
        // At top - return to default position
        this.header.style.transform = 'translateY(0)';
        this.header.classList.remove('ld-header-03--hidden', 'ld-header-03--fixed');
        isHeaderVisible = true;
        isHeaderFixed = false;
      }
      
      // Update scroll state for styling (background, colors, etc.)
      if (scrollTop > scrollThreshold) {
        this.header.classList.add('ld-header-03--scrolled');
        // Only switch logo in default mode (not in white or black mode)
        if (colorState === 'white') {
          // White mode: keep white logo, no switching
        } else if (colorState === 'black') {
          // Black mode: keep black logo, no switching
        } else {
          // Default mode: switch to black logo
          this.switchLogo('black');
        }
      } else {
        this.header.classList.remove('ld-header-03--scrolled');
        // Set logo based on color state
        if (colorState === 'white') {
          this.switchLogo('white');
        } else if (colorState === 'black') {
          this.switchLogo('black');
        } else {
          this.switchLogo('white');
        }
      }
      
      lastScrollTop = scrollTop;
      lastScrollTime = currentTime;
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });
    
    // Add touch event handling for mobile devices
    if ('ontouchstart' in window) {
      let touchStartY = 0;
      let touchEndY = 0;
      
      this.header.addEventListener('touchstart', (e) => {
        touchStartY = e.touches[0].clientY;
      }, { passive: true });
      
      this.header.addEventListener('touchend', (e) => {
        touchEndY = e.changedTouches[0].clientY;
        const touchDiff = touchStartY - touchEndY;
        
        // If user swipes up on header, show it
        if (touchDiff > 50 && !isHeaderVisible) {
          this.header.style.transform = 'translateY(0)';
          this.header.classList.remove('ld-header-03--hidden');
          this.header.classList.add('ld-header-03--fixed');
          isHeaderVisible = true;
          isHeaderFixed = true;
        }
      }, { passive: true });
    }
    
    // Initial check
    handleScroll();
  }

  switchLogo(type) {
    if (!this.logoImage) return;
    
    const whiteLogo = this.logoImage.getAttribute('data-logo-white');
    const blackLogo = this.logoImage.getAttribute('data-logo-black');
    
    if (type === 'black' && blackLogo) {
      this.logoImage.src = blackLogo;
    } else if (type === 'white' && whiteLogo) {
      this.logoImage.src = whiteLogo;
    }
  }

  setupEventListeners() {
    // Early return if dropdown elements don't exist
    if (!this.overlay || this.toggles.length === 0 || this.submenus.length === 0) {
      return;
    }

    // Check if we're on desktop (screen width > 1120px)
    const isDesktop = () => window.innerWidth > 1120;

    // Simple dropdown state management
    let currentOpenDropdown = null;
    let closeTimeout = null;

    // Toggle buttons
    this.toggles.forEach(toggle => {
      // Desktop: hover events
      if (isDesktop()) {
        toggle.addEventListener('mouseenter', (event) => {
          // Clear any pending close timeout
          if (closeTimeout) {
            clearTimeout(closeTimeout);
            closeTimeout = null;
          }
          
          const submenuId = toggle.getAttribute('aria-controls');
          this.closeAllDropdowns();
          this.openDropdown(toggle, submenuId);
          currentOpenDropdown = toggle;
        });

        // Close on mouse leave with small delay
        toggle.addEventListener('mouseleave', (event) => {
          // Set a small delay to allow moving to submenu
          closeTimeout = setTimeout(() => {
            // Check if mouse is still over the navigation area
            const relatedTarget = event.relatedTarget;
            if (!relatedTarget || 
                (!relatedTarget.closest('.ld-header-03__nav-item') && 
                 !relatedTarget.closest('.ld-header-03__dropdown-overlay'))) {
              this.closeAllDropdowns();
              currentOpenDropdown = null;
            }
          }, 150);
        });
      }

      // Mobile: click events (preserved for mobile)
      toggle.addEventListener('click', (event) => {
        // Only handle clicks on mobile
        if (isDesktop()) {
          return; // Skip click handling on desktop
        }
        
        event.preventDefault();
        event.stopPropagation();
        
        const menuId = toggle.getAttribute('data-menu-id');
        const submenuId = toggle.getAttribute('aria-controls');
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        
        if (isExpanded) {
          this.closeDropdown(toggle, submenuId);
        } else {
          this.closeAllDropdowns();
          this.openDropdown(toggle, submenuId);
        }
      });

      // Keyboard navigation (works on both desktop and mobile)
      toggle.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggle.click();
        }
      });
    });

    // Handle submenu hover on desktop
    if (isDesktop()) {
      this.submenus.forEach(submenu => {
        submenu.addEventListener('mouseenter', (event) => {
          // Clear any pending close timeout when entering submenu
          if (closeTimeout) {
            clearTimeout(closeTimeout);
            closeTimeout = null;
          }
        });

        submenu.addEventListener('mouseleave', (event) => {
          // Set delay to allow moving back to toggle
          closeTimeout = setTimeout(() => {
            const relatedTarget = event.relatedTarget;
            if (!relatedTarget || 
                (!relatedTarget.closest('.ld-header-03__nav-item') && 
                 !relatedTarget.closest('.ld-header-03__dropdown-overlay'))) {
              this.closeAllDropdowns();
              currentOpenDropdown = null;
            }
          }, 150);
        });
      });

      // Handle overlay mouse leave
      if (this.overlay) {
        this.overlay.addEventListener('mouseleave', (event) => {
          // Close dropdown when mouse leaves the overlay area
          closeTimeout = setTimeout(() => {
            this.closeAllDropdowns();
            currentOpenDropdown = null;
          }, 100);
        });
      }
    }

    // Close dropdown when clicking outside
    document.addEventListener('click', (event) => {
      if (!event.target.closest('.ld-header-03__nav') && 
          !event.target.closest('.ld-header-03__dropdown-overlay')) {
        this.closeAllDropdowns();
        currentOpenDropdown = null;
      }
    });

    // Handle escape key
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeAllDropdowns();
        currentOpenDropdown = null;
      }
    });

    // Close dropdown when scrolling
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.closeAllDropdowns();
        currentOpenDropdown = null;
      }, 100);
    });

    // Handle window resize to switch between hover/click modes
    window.addEventListener('resize', () => {
      // Close all dropdowns when switching between desktop/mobile
      this.closeAllDropdowns();
      currentOpenDropdown = null;
    });
  }

  openDropdown(toggle, submenuId) {
    const submenu = document.getElementById(submenuId);
    if (!submenu) return;

    // Update toggle state
    toggle.setAttribute('aria-expanded', 'true');
    
    // Show overlay and submenu
    this.overlay.hidden = false;
    submenu.hidden = false;
    
    // Apply active color styling via JavaScript
    this.applyActiveColor(toggle);
    
    // Focus first link in submenu (only on mobile/tablet, not on desktop hover)
    if (window.innerWidth <= 1120) {
      const firstLink = submenu.querySelector('.ld-header-03__submenu-link');
      if (firstLink) {
        setTimeout(() => firstLink.focus(), 0);
      }
    }

    // Add body scroll lock (only on mobile/tablet, not on desktop)
    if (window.innerWidth <= 1120) {
      document.body.style.overflow = 'hidden';
    }
  }

  closeDropdown(toggle, submenuId) {
    const submenu = document.getElementById(submenuId);
    if (!submenu) return;

    // Update toggle state
    toggle.setAttribute('aria-expanded', 'false');
    
    // Remove active color styling
    this.removeActiveColor(toggle);
    
    // Hide submenu
    submenu.hidden = true;
    
    // Hide overlay if no other submenus are open
    const visibleSubmenus = Array.from(this.submenus).filter(submenu => !submenu.hidden);
    if (visibleSubmenus.length === 0) {
      this.overlay.hidden = true;
      // Restore body scroll (only on mobile/tablet, not on desktop)
      if (window.innerWidth <= 1120) {
        document.body.style.overflow = '';
      }
    }
  }

  closeAllDropdowns() {
    this.toggles.forEach(toggle => {
      const submenuId = toggle.getAttribute('aria-controls');
      if (submenuId) {
        this.closeDropdown(toggle, submenuId);
      }
    });
  }

  /**
   * Apply active color styling for dropdown toggle buttons
   * Now handled by CSS - just add/remove active class
   */
  applyActiveColor(toggle) {
    toggle.classList.add('ld-header-03__nav-toggle--active');
  }

  /**
   * Remove active color styling
   */
  removeActiveColor(toggle) {
    toggle.classList.remove('ld-header-03__nav-toggle--active');
  }

  /**
   * Update active colors when color state or scroll state changes
   * Now handled by CSS automatically
   */
  updateActiveColors() {
    // CSS handles all color logic automatically
  }

  /**
   * Setup mobile menu event listeners
   */
  setupMobileEventListeners() {
    if (!this.burger || !this.mobileOverlay) {
      return;
    }

    // Burger button click
    this.burger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const isExpanded = this.burger.getAttribute('aria-expanded') === 'true';
      if (isExpanded) {
        this.closeMobileMenu();
      } else {
        this.openMobileMenu();
      }
    });

    // Mobile deep submenu toggle (now div, not button)
    const mobileSubmenuToggles = document.querySelectorAll('.ld-header-03__mobile-submenu-toggle[data-has-submenu="true"]');
    mobileSubmenuToggles.forEach(toggle => {
      toggle.addEventListener('click', (event) => {
        // Only handle clicks on the toggle element itself, not on child links
        if (event.target.closest('.ld-header-03__mobile-deep-submenu-item')) {
          return; // Let the link handle its own navigation
        }
        event.preventDefault();
        event.stopPropagation();
        const deepSubmenu = toggle.querySelector('.ld-header-03__mobile-deep-submenu[data-deep-submenu="true"]');
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        // Закрыть все остальные
        mobileSubmenuToggles.forEach(otherToggle => {
          if (otherToggle !== toggle) {
            otherToggle.setAttribute('aria-expanded', 'false');
            const otherDeep = otherToggle.querySelector('.ld-header-03__mobile-deep-submenu[data-deep-submenu="true"]');
            if (otherDeep) otherDeep.classList.remove('is-open');
          }
        });
        if (isExpanded) {
          toggle.setAttribute('aria-expanded', 'false');
          if (deepSubmenu) deepSubmenu.classList.remove('is-open');
        } else {
          toggle.setAttribute('aria-expanded', 'true');
          if (deepSubmenu) deepSubmenu.classList.add('is-open');
        }
      });
      // Клавиатурная поддержка
      toggle.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggle.click();
        }
      });
    });

    // Add specific event listeners for deep submenu items
    const deepSubmenuItems = document.querySelectorAll('.ld-header-03__mobile-deep-submenu-item');
    deepSubmenuItems.forEach(item => {
      item.addEventListener('click', (event) => {
        // Let the default link behavior happen
      });
    });

    // Mobile nav items with submenus
    const mobileNavItems = document.querySelectorAll('.ld-header-03__mobile-nav-item[data-has-submenu="true"]');
    mobileNavItems.forEach(navItem => {
      const header = navItem.querySelector('.ld-header-03__mobile-nav-item-header');
      if (header) {
        header.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          
          const isExpanded = navItem.classList.contains('is-open');
          
          // Close all other nav items
          mobileNavItems.forEach(otherNavItem => {
            if (otherNavItem !== navItem) {
              otherNavItem.classList.remove('is-open');
            }
          });
          
          // Toggle current nav item
          if (isExpanded) {
            navItem.classList.remove('is-open');
          } else {
            navItem.classList.add('is-open');
          }
        });
      }
    });

    // Close mobile menu when clicking outside
    document.addEventListener('click', (event) => {
      if (!event.target.closest('.ld-header-03__mobile-overlay') && 
          !event.target.closest('.ld-header-03__burger')) {
        this.closeMobileMenu();
      }
    });

    // Handle escape key
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeMobileMenu();
      }
    });

    // Close mobile menu when scrolling
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.closeMobileMenu();
      }, 100);
    });
  }

  /**
   * Open mobile menu
   */
  openMobileMenu() {
    this.burger.setAttribute('aria-expanded', 'true');
    this.mobileOverlay.setAttribute('aria-hidden', 'false');
    this.header.classList.add('ld-header-03--mobile-menu-open');
    document.body.style.overflow = 'hidden';
    
    // Reinitialize default mobile menu states when opening
    this.initializeDefaultMobileMenuStates();
  }

  /**
   * Close mobile menu
   */
  closeMobileMenu() {
    this.burger.setAttribute('aria-expanded', 'false');
    this.mobileOverlay.setAttribute('aria-hidden', 'true');
    this.header.classList.remove('ld-header-03--mobile-menu-open');
    document.body.style.overflow = '';
    
    // Close all mobile deep submenus
    const mobileSubmenuToggles = document.querySelectorAll('.ld-header-03__mobile-submenu-toggle');
    mobileSubmenuToggles.forEach(toggle => {
      toggle.setAttribute('aria-expanded', 'false');
      const deepSubmenu = toggle.querySelector('.ld-header-03__mobile-deep-submenu[data-deep-submenu="true"]');
      if (deepSubmenu) {
        deepSubmenu.classList.remove('is-open');
      }
    });
    
    // Close all mobile nav items with submenus
    const mobileNavItems = document.querySelectorAll('.ld-header-03__mobile-nav-item[data-has-submenu="true"]');
    mobileNavItems.forEach(navItem => {
      navItem.classList.remove('is-open');
    });
  }

  /**
   * Initialize color state management for mobile elements
   * Now handled by CSS automatically
   */
  initColorStateManagement() {
    // CSS handles all color logic automatically
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new LDHeaderDropdown();
  });
} else {
  new LDHeaderDropdown();
} 